import { addDays, format } from "date-fns";
import {
    GetNewsEditRealestates,
    GetNewsEditRealestates_currentRealestate
} from "src/api/generated/GetNewsEditRealestates";
import { ISelectableRealestate } from "src/stores/RealestateStore";
import { isCondominiumNews } from "src/utils/News";

const DATE_INPUT_FORMAT = "yyyy-MM-dd";

export const getDateInputValue = (date: Date): string => format(date, DATE_INPUT_FORMAT);

export const getTomorrowDateInputValue = (date: Date = new Date()): string => getDateInputValue(addDays(date, 1));

export const isPublishfromDateInvalid = (publishfrom?: string | null, today: Date = new Date()): boolean => {
    if (!publishfrom) return false;

    return publishfrom <= getDateInputValue(today);
};

export interface INewsRealestateSelection {
    realestates: ISelectableRealestate[];
    // Realestates of the news the user has no access to. They are not shown, but kept when saving.
    inaccessibleRealestateIds: string[];
    // A STWEG news is for coowners, any other news for tenants.
    isCondominium: boolean;
}

const toSelectableRealestate = (realestate: GetNewsEditRealestates_currentRealestate): ISelectableRealestate => ({
    id: realestate.id,
    name: realestate.name,
    number: realestate.number,
    consolidationid: realestate.consolidationid ?? undefined
});

// An existing news keeps its realestates. A new news starts with the realestate it was opened from, or without
// a realestate when it was opened from the page "News".
export const getInitialNewsRealestateSelection = ({
    newsrealestates,
    currentRealestate
}: GetNewsEditRealestates): INewsRealestateSelection => {
    const isCondominium = isCondominiumNews([
        ...newsrealestates.map(({ realestate }) => realestate),
        ...currentRealestate
    ]);

    if (newsrealestates.length === 0) {
        return {
            realestates: currentRealestate.map(toSelectableRealestate),
            inaccessibleRealestateIds: [],
            isCondominium
        };
    }

    return {
        realestates: newsrealestates.flatMap(({ realestate }) =>
            realestate ? [toSelectableRealestate(realestate)] : []
        ),
        inaccessibleRealestateIds: newsrealestates
            .filter(({ realestate }) => !realestate)
            .map(({ realestateid }) => realestateid),
        isCondominium
    };
};

// Micro.News adds the old field realestateid to the list, so with the flag only the list is sent.
// Otherwise the realestate from the URL could never be removed. Without the flag the mask is only reachable
// from a realestate, so realestateid is always set then.
export const getNewsRealestateRequestFields = (
    hasFeatureFlagNews: boolean,
    realestateid: string | undefined,
    realestates: ISelectableRealestate[],
    inaccessibleRealestateIds: string[]
) =>
    hasFeatureFlagNews
        ? { realestateids: [...realestates.map((realestate) => realestate.id), ...inaccessibleRealestateIds] }
        : { realestateid };
